/**
 * Value Package (Incentivazione) — contratti di RISPOSTA condivisi BE↔FE
 * (schema Zod + `z.infer`, docs/zod-boundary.md § Risposte API).
 *
 * Copre le letture della superficie `/incentivazione/value-packages`:
 * riga lista (teste di catena), riga storico versioni, dettaglio (identità +
 * basi prodotto arricchite), utilizzo nelle istanze configuratore, copertura
 * listini, analisi salute, impatto pre-publish, run di ricalcolo e anteprima
 * runtime L2. Il backend annota i service con questi tipi; i frontend li usano
 * come generic di `apiClient.*<T>` — nessuna interface locale duplicata.
 *
 * Convenzioni wire:
 * - numeric Postgres castati `::float8` nel SELECT → `z.number()`;
 * - date/timestamp → stringhe (DATE castate `::text` = `YYYY-MM-DD`,
 *   timestamptz serializzate ISO da JSON.stringify);
 * - i JSON persistiti dal worker di ricalcolo (`progress`, `report`,
 *   `impact_snapshot`) sono storicamente aperti: `.passthrough()` sulle
 *   righe che hanno avuto varianti (es. `deltas` dei dry-run), mai
 *   restringimento retroattivo.
 */
import { z } from 'zod';
import type { CommissioningValuePackage, CommissioningVpProductBaseDetail } from '../types/commissioning';
import { COMMISSIONING_VP_EVENT_FAMILIES } from '../types/commissioning';
import { commissioningFunctionEffectSchema } from './commissioning-kernel';

const uuid = z.string().uuid();

// ---------------------------------------------------------------------------
// Identità package
// ---------------------------------------------------------------------------

/** Stato salute dell'analisi (Stage D). */
export const COMMISSIONING_VP_HEALTH_STATES = ['aligned', 'review', 'blocking'] as const;
export type CommissioningVpHealthState = typeof COMMISSIONING_VP_HEALTH_STATES[number];

/**
 * Riga `commissioning_value_packages` (PACKAGE_COLS del service). Il
 * `satisfies` tiene lo schema allineato all'interface storica
 * `CommissioningValuePackage` (types/commissioning): ogni campo dell'interface
 * deve esistere qui con tipo compatibile.
 */
export const commissioningValuePackageSchema = z.object({
  id: uuid,
  tenant_id: uuid,
  managed_by_org_id: uuid.nullable(),
  name: z.string(),
  description: z.string().nullable(),
  event_family: z.enum(COMMISSIONING_VP_EVENT_FAMILIES),
  published_at: z.string().nullable(),
  valid_from: z.string().nullable(),
  valid_to: z.string().nullable(),
  is_active: z.boolean(),
  version_group_id: uuid,
  version: z.number().int().positive(),
  is_current_version: z.boolean(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  last_analysis_at: z.string().nullable(),
  analysis_health: z.enum(COMMISSIONING_VP_HEALTH_STATES).nullable(),
  superseded_at: z.string().nullable(),
  superseded_by_version_id: uuid.nullable(),
  superseded_by_user_id: uuid.nullable(),
  correction_of_version_id: uuid.nullable(),
}).strict() satisfies z.ZodType<CommissioningValuePackage>;
export type CommissioningValuePackageDto = z.infer<typeof commissioningValuePackageSchema>;

/** Riga elenco teste di catena (`GET /value-packages`). */
export const commissioningValuePackageListRowSchema = commissioningValuePackageSchema.extend({
  product_count: z.number().int().nonnegative(),
  versions_count: z.number().int().nonnegative(),
  /** Id della bozza di correzione pendente nella catena (null = nessuna). */
  pending_correction_id: uuid.nullable(),
}).strict();
export type CommissioningValuePackageListRow = z.infer<typeof commissioningValuePackageListRowSchema>;

/** Riga storico versioni della catena (`GET /value-packages/:id/versions`). */
export const commissioningValuePackageVersionRowSchema = commissioningValuePackageSchema.extend({
  product_count: z.number().int().nonnegative(),
  /** Nome di chi ha pubblicato la correzione sostitutiva (best-effort, può essere null). */
  superseded_by_user_name: z.string().nullable(),
  /** La finestra della versione interseca una gara attiva/in corso che usa la catena. */
  in_use_by_active_competitions: z.boolean(),
}).strict();
export type CommissioningValuePackageVersionRow = z.infer<typeof commissioningValuePackageVersionRowSchema>;

/**
 * Base prodotto arricchita del dettaglio (PRODUCT_COLS + descrittivi live:
 * fornitore del listino sorgente, categoria e natura correnti del prodotto).
 */
export const commissioningVpProductBaseDetailSchema = z.object({
  id: uuid,
  tenant_id: uuid,
  package_id: uuid,
  product_id: uuid,
  product_name: z.string(),
  source_price_list_id: uuid.nullable(),
  source_price_list_name: z.string().nullable(),
  valenza: z.number(),
  gettone_contrattuale: z.number(),
  gettone_gara: z.number(),
  canone_multiplier: z.number().nullable(),
  canone_base_offerta: z.number().nullable(),
  canone_gara: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  supplier_id: uuid.nullable(),
  supplier_name: z.string().nullable(),
  category_id: uuid.nullable(),
  category_name: z.string().nullable(),
  product_nature: z.string().nullable(),
}).strict() satisfies z.ZodType<CommissioningVpProductBaseDetail>;
export type CommissioningVpProductBaseDetailDto = z.infer<typeof commissioningVpProductBaseDetailSchema>;

/** Dettaglio package: identità + basi prodotto arricchite (get/create/replaceProducts/duplicate/versions/corrections). */
export const commissioningValuePackageDetailSchema = commissioningValuePackageSchema.extend({
  products: z.array(commissioningVpProductBaseDetailSchema),
}).strict();
export type CommissioningValuePackageDetail = z.infer<typeof commissioningValuePackageDetailSchema>;

// ---------------------------------------------------------------------------
// Utilizzo nelle istanze configuratore
// ---------------------------------------------------------------------------

/** Funzione abbinata a un'istanza configuratore (vista "Utilizzo", read-only). */
export const commissioningVpInstanceUsageFunctionSchema = z.object({
  assignmentId: uuid,
  functionId: uuid,
  functionName: z.string(),
  sequence: z.number().int(),
  matchLevers: z.array(commissioningFunctionEffectSchema),
  elseLevers: z.array(commissioningFunctionEffectSchema),
}).strict();
export type CommissioningVpInstanceUsageFunction = z.infer<typeof commissioningVpInstanceUsageFunctionSchema>;

/**
 * Istanza configuratore (con gara) che usa il VP come default di cluster o
 * come VP specifico di un driver (`GET /value-packages/:id/instances`).
 */
export const commissioningVpInstanceUsageSchema = z.object({
  instanceId: uuid,
  instanceName: z.string(),
  competitionId: uuid,
  competitionName: z.string(),
  /** Funzioni abbinate all'istanza, in ordine di sequenza. */
  functions: z.array(commissioningVpInstanceUsageFunctionSchema),
}).strict();
export type CommissioningVpInstanceUsage = z.infer<typeof commissioningVpInstanceUsageSchema>;

// ---------------------------------------------------------------------------
// Copertura listini (Stage D)
// ---------------------------------------------------------------------------

/** Prodotto classificato nel riepilogo copertura. */
export const commissioningVpCoverageProductItemSchema = z.object({
  productId: uuid,
  productName: z.string(),
  sku: z.string().nullable(),
  monthlyFee: z.number().nullable(),
  productNature: z.string(),
  categoryId: uuid.nullable(),
  categoryName: z.string().nullable(),
  driverId: uuid.nullable(),
  driverName: z.string().nullable(),
  coveredByPriceListId: uuid.nullable(),
}).strict();
export type CommissioningVpCoverageProductItem = z.infer<typeof commissioningVpCoverageProductItemSchema>;

/** Riepilogo per listino sorgente. */
export const commissioningVpCoveragePriceListSchema = z.object({
  priceListId: uuid,
  priceListName: z.string(),
  priceListType: z.string(),
  supplierId: uuid.nullable(),
  supplierName: z.string().nullable(),
  included: z.array(commissioningVpCoverageProductItemSchema),
  coveredViaOther: z.array(commissioningVpCoverageProductItemSchema),
  missing: z.array(commissioningVpCoverageProductItemSchema),
  notEligible: z.array(commissioningVpCoverageProductItemSchema),
}).strict();
export type CommissioningVpCoveragePriceList = z.infer<typeof commissioningVpCoveragePriceListSchema>;

/** Sommario globale copertura. */
export const commissioningVpCoverageSummarySchema = z.object({
  covered: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
  notEligible: z.number().int().nonnegative(),
  /**
   * Percentuale copertura sui prodotti eleggibili DISTINCT. null = denominatore
   * 0 (nessun listino sorgente nelle basi): stato neutro, MAI da presentare
   * come 100%.
   */
  coveragePct: z.number().nullable(),
  effectiveDate: z.string().nullable(),
}).strict();
export type CommissioningVpCoverageSummary = z.infer<typeof commissioningVpCoverageSummarySchema>;

/** `GET /value-packages/:id/coverage`. */
export const commissioningVpCoverageResultSchema = z.object({
  summary: commissioningVpCoverageSummarySchema,
  priceLists: z.array(commissioningVpCoveragePriceListSchema),
}).strict();
export type CommissioningVpCoverageResult = z.infer<typeof commissioningVpCoverageResultSchema>;

// ---------------------------------------------------------------------------
// Analisi salute (Stage D)
// ---------------------------------------------------------------------------

/** Prodotto con drift del canone fotografato rispetto al listino corrente. */
export const commissioningVpDriftItemSchema = z.object({
  productId: uuid,
  productName: z.string(),
  sourcePriceListId: uuid.nullable(),
  canonePhotographed: z.number().nullable(),
  canoneCurrent: z.number().nullable(),
  driftPct: z.number().nullable(),
  hasDrift: z.boolean(),
}).strict();
export type CommissioningVpDriftItem = z.infer<typeof commissioningVpDriftItemSchema>;

/** Fotografia persistita dell'analisi (`analysis_snapshot`). */
export const commissioningVpAnalysisSnapshotSchema = z.object({
  health: z.enum(COMMISSIONING_VP_HEALTH_STATES),
  /** null = copertura indeterminata (nessun listino sorgente). */
  coveragePct: z.number().nullable(),
  covered: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
  hasDrift: z.boolean(),
  driftCount: z.number().int().nonnegative(),
  productCount: z.number().int().nonnegative(),
  computedAt: z.string(),
}).strict();
export type CommissioningVpAnalysisSnapshot = z.infer<typeof commissioningVpAnalysisSnapshotSchema>;

/** `POST /value-packages/:id/analysis/refresh`. */
export const commissioningVpAnalysisResultSchema = z.object({
  health: z.enum(COMMISSIONING_VP_HEALTH_STATES),
  coverage: commissioningVpCoverageResultSchema,
  driftItems: z.array(commissioningVpDriftItemSchema),
  hasDrift: z.boolean(),
  lastAnalysisAt: z.string(),
  snapshot: commissioningVpAnalysisSnapshotSchema,
}).strict();
export type CommissioningVpAnalysisResult = z.infer<typeof commissioningVpAnalysisResultSchema>;

// ---------------------------------------------------------------------------
// Impatto pre-publish (Stage E)
// ---------------------------------------------------------------------------

export const commissioningVpImpactInstanceSummarySchema = z.object({
  instance_id: uuid,
  instance_name: z.string(),
  /** Id del VP referenziato dall'istanza (può essere una versione precedente). */
  value_package_id: uuid,
  events_count: z.number().int().nonnegative(),
  amount_eur: z.number(),
}).strict();
export type CommissioningVpImpactInstanceSummary = z.infer<typeof commissioningVpImpactInstanceSummarySchema>;

export const commissioningVpImpactCompetitionSummarySchema = z.object({
  competition_id: uuid,
  competition_name: z.string(),
  family: z.string(),
  status: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  instances: z.array(commissioningVpImpactInstanceSummarySchema),
  events_count: z.number().int().nonnegative(),
  amount_eur: z.number(),
  has_locked_periods: z.boolean(),
}).strict();
export type CommissioningVpImpactCompetitionSummary = z.infer<typeof commissioningVpImpactCompetitionSummarySchema>;

/** `GET /value-packages/:id/impact` (e payload `impact` dei 202/409 di publish). */
export const commissioningVpImpactAnalysisSchema = z.object({
  /** Quante gare attive usano questo VP (via version_group). */
  affected_competitions_count: z.number().int().nonnegative(),
  competitions: z.array(commissioningVpImpactCompetitionSummarySchema),
  /** true se almeno una gara ha periodi lockati → il ricalcolo userà carry-forward. */
  has_locked_periods: z.boolean(),
  total_events: z.number().int().nonnegative(),
  total_amount_eur: z.number(),
  /** Hash dello snapshot (package_id + competition_ids + instance_ids + effective_date). */
  snapshot_hash: z.string(),
  /** Data efficacia suggerita (YYYY-MM-DD). */
  suggested_effective_date: z.string(),
}).strict();
export type CommissioningVpImpactAnalysis = z.infer<typeof commissioningVpImpactAnalysisSchema>;

// ---------------------------------------------------------------------------
// Run di ricalcolo (Stage E)
// ---------------------------------------------------------------------------

export const COMMISSIONING_VP_RECALC_POLICIES = ['from_effective_date', 'from_competition_start'] as const;
export type CommissioningVpRecalcPolicy = typeof COMMISSIONING_VP_RECALC_POLICIES[number];

export const COMMISSIONING_VP_RECALC_STATUSES = [
  'pending', 'running', 'completed', 'partial', 'blocked', 'cancelled', 'snapshot_conflict',
] as const;
export type CommissioningVpRecalcStatus = typeof COMMISSIONING_VP_RECALC_STATUSES[number];

/** Avanzamento del worker (JSONB `progress`, aggiornato per gara). */
export const commissioningVpRecalcRunProgressSchema = z.object({
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
  current_competition_id: uuid.nullable(),
}).passthrough();
export type CommissioningVpRecalcRunProgress = z.infer<typeof commissioningVpRecalcRunProgressSchema>;

/**
 * Esito per gara nel report finale. `.passthrough()`: i report dei dry-run
 * hanno storicamente trasportato dettagli aggiuntivi (es. `deltas`) che
 * restano lossless sul wire.
 */
export const commissioningVpRecalcCompetitionReportSchema = z.object({
  competition_id: uuid,
  competition_name: z.string(),
  events_total: z.number().int().nonnegative(),
  events_changed: z.number().int().nonnegative(),
  delta_amount_eur: z.number(),
  has_locked_periods: z.boolean(),
  error: z.string().nullable(),
}).passthrough();
export type CommissioningVpRecalcCompetitionReport = z.infer<typeof commissioningVpRecalcCompetitionReportSchema>;

export const commissioningVpRecalcRunReportSchema = z.object({
  competitions: z.array(commissioningVpRecalcCompetitionReportSchema),
  summary: z.object({
    events_total: z.number().int().nonnegative(),
    events_changed: z.number().int().nonnegative(),
    delta_amount_eur: z.number(),
    blocked: z.number().int().nonnegative(),
    errors: z.number().int().nonnegative(),
  }).passthrough(),
}).passthrough();
export type CommissioningVpRecalcRunReport = z.infer<typeof commissioningVpRecalcRunReportSchema>;

/**
 * Riga `commissioning_recalc_runs` di un run Value Package (package_id NOT
 * NULL). `effective_date` è una DATE castata `::text` (YYYY-MM-DD); i
 * timestamp sono ISO.
 */
export const commissioningVpRecalcRunSchema = z.object({
  id: uuid,
  tenant_id: uuid,
  package_id: uuid,
  version_group_id: uuid,
  competition_ids: z.array(uuid),
  policy: z.enum(COMMISSIONING_VP_RECALC_POLICIES),
  effective_date: z.string(),
  dry_run: z.boolean(),
  status: z.enum(COMMISSIONING_VP_RECALC_STATUSES),
  confirmed_snapshot_hash: z.string().nullable(),
  /**
   * Snapshot dell'analisi impatto al momento della creazione del run (il
   * worker verifica contro questo, mai contro lo stato live). null per run
   * creati prima dell'introduzione della colonna.
   */
  impact_snapshot: commissioningVpImpactAnalysisSchema.passthrough().nullable(),
  progress: commissioningVpRecalcRunProgressSchema,
  report: commissioningVpRecalcRunReportSchema.nullable(),
  created_by: uuid.nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
}).strict();
export type CommissioningVpRecalcRun = z.infer<typeof commissioningVpRecalcRunSchema>;

/**
 * `POST /value-packages/:id/publish` (200): dettaglio pubblicato più, quando
 * il publish ha richiesto conferma d'impatto, l'analisi confermata e il run
 * di ricalcolo creato nella stessa unità di lavoro.
 */
export const commissioningValuePackagePublishResultSchema = commissioningValuePackageDetailSchema.extend({
  impact: commissioningVpImpactAnalysisSchema.optional(),
  recalcRun: commissioningVpRecalcRunSchema.optional(),
}).strict();
export type CommissioningValuePackagePublishResult = z.infer<typeof commissioningValuePackagePublishResultSchema>;

// ---------------------------------------------------------------------------
// Anteprima runtime L2 (`POST /value-packages/:id/preview`)
// ---------------------------------------------------------------------------

/**
 * Base prodotto fotografata usata dall'anteprima: il runtime restituisce la
 * riga base piatta (shape `CommissioningVpBaseShape` del motore).
 */
export const commissioningVpPreviewBaseSchema = z.object({
  id: uuid,
  product_id: uuid,
  valenza: z.number(),
  gettone_contrattuale: z.number(),
  gettone_gara: z.number(),
  canone_multiplier: z.number().nullable(),
  canone_base_offerta: z.number().nullable(),
  canone_gara: z.number().nullable(),
}).passthrough();
export type CommissioningVpPreviewBase = z.infer<typeof commissioningVpPreviewBaseSchema>;

/** Stesso contratto del runtime L2 (`CommissioningL2Result`). */
export const commissioningVpPreviewResultSchema = z.object({
  /** null = evento non valorizzabile (vedi `notValorizableReason`). */
  productBase: commissioningVpPreviewBaseSchema.nullable(),
  /** Motivo esplicito di evento NON valorizzabile (fail-closed). */
  notValorizableReason: z.string().nullable(),
  valenza: z.number(),
  gettoneContrattuale: z.number(),
  gettoneGara: z.number(),
  /** Canone congelato: ereditato dal listino (monthly_fee) o override base. */
  canone: z.object({
    inherited: z.boolean(),
    value: z.number().nullable(),
    multiplier: z.number().nullable(),
  }).strict(),
}).strict();
export type CommissioningVpPreviewResult = z.infer<typeof commissioningVpPreviewResultSchema>;
